import React, { useState, useEffect } from "react";
import { Card, Form, Button, Table, Alert, Modal, Spinner } from "react-bootstrap";
import api from "../../../services/api";

// Componente para gerenciar horários de um funcionário específico,
// adaptado da lógica original de Horarios.js.
export default function GerenciarHorariosFuncionario({ funcionario, show, handleClose }) {
  const [horarios, setHorarios] = useState([]);
  const [bloqueios, setBloqueios] = useState([]);
  const [showHorarioModal, setShowHorarioModal] = useState(false);
  const [showBloqueioModal, setShowBloqueioModal] = useState(false);
  const [formHorario, setFormHorario] = useState({
    id: null,
    tipo: "Padrao",
    diaSemana: "1",
    horaInicio: "",
    horaFim: "",
    dataEspecifica: "",
  });
  const [formBloqueio, setFormBloqueio] = useState({
    id: null,
    dataInicio: "",
    dataFim: "",
    horaInicio: "",
    horaFim: "",
    diaTodo: false,
    // Adicionando campos para bloqueio recorrente (se necessário)
    recorrente: false,
    diaSemana: "1",
    mes: new Date().getMonth() + 1, // Mês atual
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const diasSemana = [
    "Domingo",
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
  ];

  // 🔹 Buscar horários e bloqueios
  const fetchHorarios = async () => {
    if (!funcionario?.id || !show) return;
    setLoading(true);
    setError(null);
    try {
      // Adaptação: Usar o ID do funcionário passado como prop
      const response = await api.get(`/Horarios/funcionario/${funcionario.id}`);
      setHorarios(response.data.disponibilidades || []);
      setBloqueios(response.data.bloqueios || []);
    } catch (err) {
      console.error("Erro ao carregar horários:", err);
      setError("Erro ao carregar horários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchHorarios();
    }
  }, [funcionario, show]);

  // 🔹 Controle dos inputs (adaptado do Horarios.js)
  const handleHorarioChange = (e) => {
    const { name, value } = e.target;
    setFormHorario((prev) => ({ ...prev, [name]: value }));
  };

  const handleBloqueioChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormBloqueio((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // 🔹 Salvar horário (novo ou edição) - Adaptado do Horarios.js
  const handleHorarioSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        funcionarioId: funcionario.id,
        diaSemana:
          formHorario.tipo === "Padrao"
            ? parseInt(formHorario.diaSemana)
            : null,
        dataEspecifica:
          formHorario.tipo === "Excecao" ? formHorario.dataEspecifica : null,
        horaInicio: formHorario.horaInicio + ":00",
        horaFim: formHorario.horaFim + ":00",
        tipo: formHorario.tipo,
      };

      if (formHorario.id) {
        await api.put(`/Horarios/disponibilidade/${formHorario.id}`, payload);
      } else {
        await api.post("/Horarios/disponibilidade", payload);
      }

      setShowHorarioModal(false);
      fetchHorarios();
    } catch (err) {
      setError("Erro ao salvar horário.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // 🔹 Salvar bloqueio (novo ou edição) - Adaptado do Horarios.js
  const handleBloqueioSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Adaptação: Buscar clienteMasterId do funcionário (se necessário)
      // Ou assumir que o clienteMasterId está no cookie do usuário logado (master)
      // Vamos assumir que a API de bloqueio já resolve o clienteMasterId pelo funcionarioId
      // ou que o token de autenticação do master já resolve isso.
      // Se a API exigir, precisaremos buscar o clienteMasterId.
      // Por enquanto, vamos manter a lógica de envio de bloqueio simplificada.

      const {
        dataInicio,
        dataFim,
        horaInicio,
        horaFim,
        diaTodo,
        recorrente,
        diaSemana,
        mes,
      } = formBloqueio;

      if (recorrente) {
        // Lógica de bloqueio recorrente (adaptada do Horarios.js)
        const ano = new Date().getFullYear();
        const primeiroDia = new Date(ano, mes - 1, 1);
        const ultimoDia = new Date(ano, mes, 0);

        const dias = [];
        for (let d = new Date(primeiroDia); d <= ultimoDia; d.setDate(d.getDate() + 1)) {
          if (d.getDay() === parseInt(diaSemana)) {
            dias.push(new Date(d));
          }
        }

        for (const dia of dias) {
          const payload = {
            funcionarioId: funcionario.id,
            data: dia.toISOString().split('T')[0], // Envia apenas a data
            horaInicio: diaTodo ? "00:00:00" : horaInicio + ":00",
            horaFim: diaTodo ? "23:59:59" : horaFim + ":00",
          };
          await api.post("/Horarios/bloqueio", payload);
        }
        window.globalAlert(`Bloqueio aplicado para todas as ${diasSemana[diaSemana]}s de ${new Date(ano, mes - 1).toLocaleString("pt-BR", { month: "long" })}!`, "success");
      } else {
        // Bloqueio normal (intervalo de datas)
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim || dataInicio);

        const dias = [];
        for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
          dias.push(new Date(d));
        }

        for (const dia of dias) {
          const payload = {
            funcionarioId: funcionario.id,
            data: dia.toISOString().split('T')[0], // Envia apenas a data
            horaInicio: diaTodo ? "00:00:00" : horaInicio + ":00",
            horaFim: diaTodo ? "23:59:59" : horaFim + ":00",
          };
          await api.post("/Horarios/bloqueio", payload);
        }
        window.globalAlert("Bloqueio(s) adicionado(s) com sucesso!", "success");
      }

      setShowBloqueioModal(false);
      fetchHorarios();
    } catch (err) {
      setError("Erro ao adicionar bloqueio.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };


const handleDeleteHorario = async (id) => {
  setSubmitting(true);
  try {
    await api.delete(`/Horarios/disponibilidade/${id}`);
    fetchHorarios();
  } catch (err) {
    window.globalAlert("Erro ao excluir horário.", "error");
    console.error(err);
  } finally {
    setSubmitting(false);
  }
};

const handleDeleteBloqueio = async (id) => {
  setSubmitting(true);
  try {
    await api.delete(`/Horarios/bloqueio/${id}`);
    fetchHorarios();
  } catch (err) {
    window.globalAlert("Erro ao excluir bloqueio.", "error");
    console.error(err);
  } finally {
    setSubmitting(false);
  }
};

  // 🔹 Funções para abrir/fechar modals de formulário
  const handleShowHorarioModal = (horario = null) => {
    if (horario) {
      // Lógica de edição (simplificada, pois o Horarios.js original tinha)
      setFormHorario({
        id: horario.id,
        tipo: horario.tipo,
        diaSemana: horario.diaSemana?.toString() || "1",
        horaInicio: horario.horaInicio?.substring(0, 5),
        horaFim: horario.horaFim?.substring(0, 5),
        dataEspecifica: horario.dataEspecifica
          ? horario.dataEspecifica.split("T")[0]
          : "",
      });
    } else {
      setFormHorario({
        id: null,
        tipo: "Padrao",
        diaSemana: "1",
        horaInicio: "",
        horaFim: "",
        dataEspecifica: "",
      });
    }
    setShowHorarioModal(true);
  };

  const handleShowBloqueioModal = (bloqueio = null) => {
    if (bloqueio) {
      // Lógica de edição de bloqueio (não implementada no original, mas mantendo a estrutura)
      // O original só tinha exclusão e adição, vamos focar na adição/exclusão.
    } else {
      setFormBloqueio({
        id: null,
        dataInicio: "",
        dataFim: "",
        horaInicio: "",
        horaFim: "",
        diaTodo: false,
        recorrente: false,
        diaSemana: "1",
        mes: new Date().getMonth() + 1,
      });
    }
    setShowBloqueioModal(true);
  };

const handleCloseFormModal = () => {
  setShowHorarioModal(false);
  setShowBloqueioModal(false);
};

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Horários de {funcionario?.nome}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="d-flex justify-content-end mb-3 gap-2">
          <Button
            variant="success"
            onClick={() => handleShowHorarioModal()}
            disabled={submitting}
          >
            + Novo Horário
          </Button>
          <Button
            variant="warning"
            onClick={() => handleShowBloqueioModal()}
            disabled={submitting}
          >
            + Novo Bloqueio
          </Button>
        </div>

        {loading || submitting ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p>Carregando/Salvando...</p>
          </div>
        ) : (
          <>
            {/* 🔹 Tabela de horários */}
            <h6>Horários Cadastrados</h6>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Dia/Data</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {horarios.length > 0 ? (
                  horarios.map((h) => (
                    <tr key={h.id}>
                      <td>{h.tipo || "Padrão"}</td>
                      <td>
                        {h.tipo === "Padrao"
                          ? diasSemana[h.diaSemana ?? 0]
                          : new Date(h.dataEspecifica || h.data).toLocaleDateString("pt-BR")}
                      </td>
                      <td>{h.horaInicio?.substring(0, 5)}</td>
                      <td>{h.horaFim?.substring(0, 5)}</td>
                      <td className="d-flex gap-2">
                        {/* Mantendo a opção de editar, embora a lógica de edição no Horarios.js original fosse simples */}
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleShowHorarioModal(h)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteHorario(h.id)}
                        >
                          Excluir
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      Nenhum horário cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* 🔹 Tabela de bloqueios */}
            <h6>Bloqueios</h6>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {bloqueios.length > 0 ? (
                  bloqueios.map((b) => (
                    <tr key={b.id}>
                      <td>{new Date(b.data).toLocaleDateString("pt-BR")}</td>
                      <td>{b.horaInicio?.substring(0, 5)}</td>
                      <td>{b.horaFim?.substring(0, 5)}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteBloqueio(b.id)}
                        >
                          Excluir
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      Nenhum bloqueio cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      {/* Modal de Horário (Novo/Edição) */}
      <Modal show={showHorarioModal} onHide={handleCloseFormModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{formHorario.id ? "Editar" : "Novo"} Horário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleHorarioSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                name="tipo"
                value={formHorario.tipo}
                onChange={handleHorarioChange}
                required
              >
                <option value="Padrao">Padrão (Semanal)</option>
                <option value="Excecao">Exceção (Data Específica)</option>
              </Form.Select>
            </Form.Group>

            {formHorario.tipo === "Padrao" && (
              <Form.Group className="mb-3">
                <Form.Label>Dia da Semana</Form.Label>
                <Form.Select
                  name="diaSemana"
                  value={formHorario.diaSemana}
                  onChange={handleHorarioChange}
                  required
                >
                  {diasSemana.map((dia, index) => (
                    <option key={index} value={index}>
                      {dia}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {formHorario.tipo === "Excecao" && (
              <Form.Group className="mb-3">
                <Form.Label>Data Específica</Form.Label>
                <Form.Control
                  type="date"
                  name="dataEspecifica"
                  value={formHorario.dataEspecifica}
                  onChange={handleHorarioChange}
                  required
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Hora Início</Form.Label>
              <Form.Control
                type="time"
                name="horaInicio"
                value={formHorario.horaInicio}
                onChange={handleHorarioChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Hora Fim</Form.Label>
              <Form.Control
                type="time"
                name="horaFim"
                value={formHorario.horaFim}
                onChange={handleHorarioChange}
                required
              />
            </Form.Group>

            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Salvar"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de Bloqueio (Novo) */}
      <Modal show={showBloqueioModal} onHide={handleCloseFormModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Novo Bloqueio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleBloqueioSubmit}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Bloqueio Recorrente (Mensal)"
                name="recorrente"
                checked={formBloqueio.recorrente}
                onChange={handleBloqueioChange}
              />
            </Form.Group>

            {formBloqueio.recorrente ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Dia da Semana</Form.Label>
                  <Form.Select
                    name="diaSemana"
                    value={formBloqueio.diaSemana}
                    onChange={handleBloqueioChange}
                    required
                  >
                    {diasSemana.map((dia, index) => (
                      <option key={index} value={index}>
                        {dia}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Mês de Aplicação</Form.Label>
                  <Form.Select
                    name="mes"
                    value={formBloqueio.mes}
                    onChange={handleBloqueioChange}
                    required
                  >
                    {[...Array(12).keys()].map((i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString("pt-BR", { month: "long" })}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Data Início</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataInicio"
                    value={formBloqueio.dataInicio}
                    onChange={handleBloqueioChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Data Fim (Opcional)</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataFim"
                    value={formBloqueio.dataFim}
                    onChange={handleBloqueioChange}
                  />
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Dia Todo"
                name="diaTodo"
                checked={formBloqueio.diaTodo}
                onChange={handleBloqueioChange}
              />
            </Form.Group>

            {!formBloqueio.diaTodo && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Hora Início</Form.Label>
                  <Form.Control
                    type="time"
                    name="horaInicio"
                    value={formBloqueio.horaInicio}
                    onChange={handleBloqueioChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Hora Fim</Form.Label>
                  <Form.Control
                    type="time"
                    name="horaFim"
                    value={formBloqueio.horaFim}
                    onChange={handleBloqueioChange}
                    required
                  />
                </Form.Group>
              </>
            )}

            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Adicionar Bloqueio"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Modal>
  );
}